import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Divider
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';

const Header = ({ onSettingsClick, onNotificationsClick, onMobileMenuToggle }) => {
  const { currentUser, userProfile, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleProfileMenuClose();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleGoToProfile = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: brandColors.primary,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          onClick={onMobileMenuToggle}
          sx={{
            display: { xs: 'block', md: 'none' },
            color: brandColors.white,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Logo Image - Add your logo file to public/images/ */}
          <Box
            component="img"
            src="/images/logo.png"
            alt="Greenlight Analytics"
            sx={{
              height: 40,
              width: 'auto',
              mr: 2,
              display: { xs: 'none', sm: 'block' } // Hide on very small screens
            }}
            onError={(e) => {
              // Fallback to text if logo fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          {/* Fallback Text Logo */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              color: brandColors.white,
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              letterSpacing: '0.5px',
              display: { xs: 'block', sm: 'none' } // Show on small screens, hide on larger if logo loads
            }}
          >
            GA Platform
          </Typography>
        </Box>

        {/* Right Section - User Info & Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Notifications - Hide on very small screens */}
          <IconButton
            color="inherit"
            onClick={onNotificationsClick}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: brandColors.white,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Badge badgeContent={3} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Settings - Hide on very small screens */}
          <IconButton
            color="inherit"
            onClick={onSettingsClick}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: brandColors.white,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <SettingsIcon />
          </IconButton>

          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              display: { xs: 'none', sm: 'block' }
            }} 
          />

          {/* User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: brandColors.white,
                fontWeight: 500,
                textAlign: 'right',
                display: { xs: 'none', sm: 'block' } // Hide name on mobile
              }}
            >
              {userProfile?.displayName || currentUser?.displayName || 'User'}
            </Typography>
            
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                color: brandColors.white,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  backgroundColor: brandColors.secondary,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {(userProfile?.firstName?.[0] || userProfile?.displayName?.[0] || 'U').toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
      >
        <MenuItem onClick={handleGoToProfile}>
          <AccountCircle sx={{ mr: 2 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Header;
